import React, { useEffect, useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';


// ─── Helpers visivi ───────────────────────────────────────────────────────────
const getTipoConfig = (tipo) => {
  const t = tipo?.toUpperCase() || '';
  if (t === 'MONTATO')  return { color: 'text-emerald-500 font-black', dot: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,.5)]', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'MONTATO'  };
  if (t === 'SMONTATO') return { color: 'text-red-500 font-black',     dot: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,.5)]',       bg: 'bg-red-500/10 border-red-500/30',         label: 'SMONTATO' };
  return                       { color: 'text-slate-400 font-bold',     dot: 'bg-slate-400',                                          bg: 'bg-slate-400/10 border-slate-400/30',     label: tipo || 'N/D' };
};


const getStatoConfig = (stato) => {
  const s = stato?.toUpperCase() || '';
  if (s === 'PRONTO')            return { color: 'text-emerald-500 font-black', dot: 'bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,.5)]', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'PRONTO'         };
  if (s.includes('RIPARAZIONE')) return { color: 'text-orange-500 font-black',  dot: 'bg-orange-500',                                                      bg: 'bg-orange-500/10 border-orange-500/30',   label: 'IN RIPARAZIONE' };
  return                                 { color: 'text-slate-400 font-bold',    dot: 'bg-slate-400',                                                        bg: 'bg-slate-400/10 border-slate-400/30',     label: stato || 'N/D'   };
};


const fmtDatetime = (val) => {
  if (!val) return '---';
  try { return new Date(val).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return val; }
};


const fmtDate = (val) => {
  if (!val) return '---';
  try { return new Date(val).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return val; }
};


const toDatetimeLocal = (val) => {
  if (!val) return '';
  try {
    const d = new Date(val);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};


// ─── Sotto-componenti ─────────────────────────────────────────────────────────

const Campo = ({ label, value, mono = false, isEditing, onChange, type = 'text', name, disabled = false, placeholder = '' }) => {
  const displayValue = value ?? '';
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">
        {label}
      </span>
      {isEditing && !disabled ? (
        type === 'textarea' ? (
          <textarea
            name={name}
            value={displayValue}
            onChange={onChange}
            placeholder={placeholder}
            rows={3}
            className="bg-background border border-border/60 rounded-xl px-3 py-2 text-sm font-medium outline-none w-full transition-all focus:ring-2 focus:ring-primary/20 hover:border-primary/40 resize-none"
          />
        ) : (
          <input
            type={type}
            name={name}
            value={displayValue}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            className={`bg-background border border-border/60 rounded-xl px-3 py-2 text-sm font-bold outline-none w-full transition-all ${
              disabled
                ? 'opacity-50 cursor-not-allowed bg-muted/20'
                : 'focus:ring-2 focus:ring-primary/20 hover:border-primary/40'
            }`}
          />
        )
      ) : (
        <span className={`text-[14px] font-bold text-foreground/85 break-words leading-snug ${mono ? 'font-mono tracking-tight text-[13px]' : ''}`}>
          {displayValue || '---'}
        </span>
      )}
    </div>
  );
};


const Sezione = ({ titolo, icon, children }) => (
  <div className="flex flex-col gap-5 p-5 md:p-6 rounded-3xl bg-muted/20 border border-border/40 hover:border-primary/20 transition-colors">
    <div className="flex items-center gap-3">
      {icon && <Icon name={icon} size={14} className="text-primary/60" />}
      <span className="text-[11px] font-black uppercase tracking-[.2em] text-primary/70">{titolo}</span>
      <div className="flex-1 h-px bg-border/20" />
    </div>
    <div className="grid gap-5">{children}</div>
  </div>
);


// ─── Componente principale ────────────────────────────────────────────────────
const Detagli_Intervento = ({ item, onClose, onUpdate }) => {
  const [isEditing, setIsEditing]           = useState(false);
  const [formData, setFormData]             = useState({ ...item });
  const [loading, setLoading]               = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [notification, setNotification]     = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const utenteRef = useRef(null);

  if (!item) return null;

  // ── Escape + blocca scroll body ───────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !showDeleteConfirm) onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, showDeleteConfirm]);

  // ── Auto-dismiss toast success ────────────────────────────────────────────
  useEffect(() => {
    if (!notification) return;
    if (notification.type !== 'success') return;
    const t = setTimeout(() => setNotification(null), 4000);
    return () => clearTimeout(t);
  }, [notification]);

  // ── Genera PDF ────────────────────────────────────────────────────────────
  const generatePDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text('OrizonDesk', 14, 15);

      doc.setFontSize(18);
      doc.setTextColor(15, 23, 42);
      doc.text('SCHEDA INTERVENTO CILINDRO', 14, 25);

      doc.setFontSize(22);
      doc.setTextColor(37, 99, 235);
      const cilText = `CILINDRO: ${formData.ncilindro || 'N/D'}`.toUpperCase();
      doc.text(cilText, 14, 35);

      doc.setDrawColor(226, 232, 240);
      doc.line(14, 38, 196, 38);

      const tipo = (formData.tipointervento || '').toUpperCase();

      const tableRows = [
        ['STATO ATTUALE', (formData.stato || '---').toUpperCase()],
        ['TIPO INTERVENTO', tipo],
        ['NUMERO CILINDRO', formData.ncilindro || '---'],
        ['LINEA', formData.linea || '---'],
        ['COLATA', formData.colata || '---'],
        ['CARRO / PANIERA', formData.carropaniera || '---'],
        ['DATA INTERVENTO', fmtDatetime(formData.dataintervento)],
        ['DATA RIENTRO', fmtDate(formData.datarientro)],
        ['NOTE INTERVENTO', formData.note || '---'],
      ];

      autoTable(doc, {
        startY: 45,
        head: [['PARAMETRO', 'VALORE']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [15, 23, 42], fontSize: 10, fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 6, overflow: 'linebreak' },
        columnStyles: {
          0: { cellWidth: 50, fontStyle: 'bold', textColor: [80, 80, 80] },
          1: { cellWidth: 'auto' }
        },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            const cellText = data.cell.raw;
            if (cellText === 'SMONTATO')  data.cell.styles.textColor = [220, 38, 38];
            if (cellText === 'MONTATO')   data.cell.styles.textColor = [16, 185, 129];
            if (cellText === 'RIENTRATO') data.cell.styles.textColor = [147, 51, 234];
            if (['SMONTATO', 'MONTATO', 'RIENTRATO'].includes(cellText)) {
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
        didDrawPage: (data) => {
          const pageSize = doc.internal.pageSize;
          const pageW = pageSize.width || pageSize.getWidth();
          const pageH = pageSize.height || pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(`Stampato il: ${new Date().toLocaleString('it-IT')}`, 14, pageH - 10);
          const pagStr = `Pagina ${doc.internal.getNumberOfPages()}`;
          doc.text(pagStr, pageW / 2, pageH - 10, { align: 'center' });
          doc.text('https://desk.orizon-aix.com', pageW - 14, pageH - 10, { align: 'right' });
        },
      });

      doc.save(`Scheda_Intervento_${formData.ncilindro || 'ND'}.pdf`);
    } catch (err) {
      console.error('PDF Error:', err);
      alert('Errore generazione PDF');
    }
  };

  // ── Gestione input ────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Click Modifica ────────────────────────────────────────────────────────
  const handleEditClick = async () => {
    if (isEditing) { handleSave(); return; }

    setIsCheckingAuth(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setNotification({ type: 'error', message: 'Devi essere loggato per modificare!' });
        return;
      }

      const { data: profil, error: dbError } = await supabase
        .from('profili')
        .select('nome, cognome, ruolo')
        .eq('id', user.id)
        .single();

      if (dbError || !profil) {
        setNotification({ type: 'error', message: 'Errore nel recupero del profil!' });
        return;
      }

      if (profil.ruolo === 'cco-admin' || profil.ruolo === 'Owner') {
        utenteRef.current = profil;
        setIsEditing(true);
      } else {
        setNotification({
          type: 'error',
          message: `${profil.nome || 'Utente'}, non sei autorizzato a modificare gli interventi!`,
        });
      }
    } catch (err) {
      setNotification({ type: 'error', message: 'Errore imprevisto!' });
    } finally {
      setIsCheckingAuth(false);
    }
  };

  // ── Salva ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { ...formData };
      if (payload.dataintervento && payload.dataintervento.includes('T') && !payload.dataintervento.includes('Z')) {
        payload.dataintervento = new Date(payload.dataintervento).toISOString();
      }
      if (!payload.datarientro) payload.datarientro = null;
      const { id, created_at, ...updatePayload } = payload;

      const { error: updateError } = await supabase
        .from('cilindripaniera')
        .update(updatePayload)
        .eq('id', item.id);

      if (updateError) throw updateError;

      setNotification({ type: 'success', message: 'Intervento aggiornato con successo!' });
      if (onUpdate) onUpdate({ ...item, ...updatePayload });
      setIsEditing(false);
    } catch (err) {
      setNotification({ type: 'error', message: 'Errore nel salvataggio: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  // ── Elimina ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setLoading(true);
    setShowDeleteConfirm(false);
    try {
      const { error } = await supabase
        .from('cilindripaniera')
        .delete()
        .eq('id', item.id);

      if (error) throw error;

      setNotification({ type: 'success', message: 'Intervento eliminato con successo!' });
      setTimeout(() => onClose(), 2500);
    } catch (err) {
      setNotification({ type: 'error', message: "Errore durante l'eliminazione: " + err.message });
      setLoading(false);
    }
  };

  // ── Annulla editing ───────────────────────────────────────────────────────
  const handleAnnulla = () => {
    setIsEditing(false);
    setFormData({ ...item });
  };

  const tipoCfg  = getTipoConfig(formData.tipointervento);
  const statoCfg = getStatoConfig(formData.stato);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6 overflow-hidden">

      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300" onClick={!showDeleteConfirm ? onClose : undefined} />

      {/* ── Toast notifica ── */}
      {notification && (
        <div className={`fixed top-8 left-1/2 -translate-x-1/2 z-[150] px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 max-w-[90vw] ${
          notification.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <Icon name={notification.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={18} className="shrink-0" />
          <span className="text-sm font-black uppercase tracking-wider">{notification.message}</span>
          <button onClick={() => setNotification(null)} className="ml-2 shrink-0 w-5 h-5 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-all">
            <Icon name="X" size={11} />
          </button>
        </div>
      )}

      {/* ── Confirm Elimina ── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative w-full max-w-sm bg-card border border-border/60 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="h-1 w-full bg-gradient-to-r from-red-500 via-red-400 to-transparent" />
            <div className="p-7 flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <span className="text-base font-black uppercase tracking-tight text-foreground">Eliminare questo intervento?</span>
                <span className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest">
                  N° Cil. {item.ncilindro || 'N/D'} — {fmtDatetime(item.dataintervento)}
                </span>
                <span className="text-[11px] text-red-500/80 font-bold">L'operazione non è reversibile.</span>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 rounded-2xl bg-muted hover:bg-muted/80 border border-border/40 text-foreground text-[11px] font-black uppercase tracking-widest transition-all">
                  Annulla
                </button>
                <button onClick={handleDelete} className="flex-1 px-4 py-2.5 rounded-2xl bg-red-500 hover:bg-red-600 text-white border border-red-600 text-[11px] font-black uppercase tracking-widest transition-all">
                  Elimina
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Pannello principale ── */}
      <div className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl bg-card border-t md:border border-border/60 rounded-t-[2.5rem] md:rounded-[2.5rem] shadow-2xl flex flex-col animate-in slide-in-from-bottom-10 md:zoom-in-95 duration-300">

        {/* Drag handle mobile */}
        <div className="md:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-12 h-1.5 rounded-full bg-muted/50" />
        </div>

        {/* ── HEADER ── */}
        <div className="shrink-0 px-6 md:px-8 pt-5 pb-6 border-b border-border/40">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-2 min-w-0 flex-1">

              <div className="flex items-center gap-2 flex-wrap">
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border ${tipoCfg.bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${tipoCfg.dot}`} />
                  <span className={`text-[10px] font-black uppercase tracking-widest ${tipoCfg.color}`}>{tipoCfg.label}</span>
                </div>
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border ${statoCfg.bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statoCfg.dot}`} />
                  <span className={`text-[10px] font-black uppercase tracking-widest ${statoCfg.color}`}>{statoCfg.label}</span>
                </div>
              </div>

              {isEditing ? (
                <input
                  name="ncilindro"
                  value={formData.ncilindro ?? ''}
                  onChange={handleInputChange}
                  className="bg-transparent border-b border-primary/30 font-black uppercase tracking-tighter text-foreground leading-[1.1] outline-none text-2xl w-full"
                  placeholder="N° Cilindro..."
                />
              ) : (
                <h3 className="font-black uppercase tracking-tighter text-foreground leading-[1.1] break-words" style={{ fontSize: 'clamp(1.4rem, 5vw, 2rem)' }}>
                  {formData.ncilindro || 'N/D'}
                </h3>
              )}

              <span className="text-[10px] font-black uppercase tracking-[.3em] text-muted-foreground/40">
                Scheda Intervento
              </span>
            </div>

            {/* ── Pulsanti header ── */}
            <div className="flex gap-1.5 shrink-0">
              {/* PDF — solo quando non si sta modificando */}
              {!isEditing && (
                <button
                  onClick={generatePDF}
                  title="Scarica PDF"
                  className="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-500 hover:bg-blue-500 hover:text-white border border-blue-500/20 flex items-center justify-center transition-all shadow-sm"
                >
                  <Icon name="FileDown" size={16} />
                </button>
              )}

              {/* Modifica / Salva (Check) */}
              <button
                onClick={handleEditClick}
                disabled={loading || isCheckingAuth}
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all shadow-sm border ${
                  isEditing
                    ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600'
                    : 'bg-muted/50 text-muted-foreground hover:text-primary border-border/40 hover:border-primary/30'
                }`}
              >
                <Icon
                  name={(loading || isCheckingAuth) ? 'Loader2' : isEditing ? 'Check' : 'Edit3'}
                  size={16}
                  className={(loading || isCheckingAuth) ? 'animate-spin' : ''}
                />
              </button>

              {/* Chiudi — visibile solo quando NON si sta modificando */}
              {!isEditing && (
                <button
                  onClick={onClose}
                  className="w-9 h-9 rounded-xl bg-muted/50 hover:bg-red-500/10 border border-border/40 hover:border-red-500/20 text-muted-foreground hover:text-red-500 flex items-center justify-center transition-all shadow-sm"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── BODY scrollabile ── */}
        <div
          className="flex-1 overflow-y-auto p-5 md:p-7 space-y-5"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}
        >

          <Sezione titolo="Tipo & Stato" icon="Activity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none">Tipo Intervento</span>
                {isEditing ? (
                  <select
                    name="tipointervento"
                    value={formData.tipointervento ?? ''}
                    onChange={handleInputChange}
                    className="bg-background border border-border/60 rounded-xl px-3 py-2.5 text-sm font-black uppercase outline-none focus:ring-2 focus:ring-primary/20 hover:border-primary/40 transition-all cursor-pointer"
                  >
                    <option value="">— Seleziona —</option>
                    <option value="MONTATO">MONTATO</option>
                    <option value="SMONTATO">SMONTATO</option>
                  </select>
                ) : (
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border w-fit ${tipoCfg.bg}`}>
                    <span className={`w-2 h-2 rounded-full ${tipoCfg.dot}`} />
                    <span className={`text-sm font-black uppercase tracking-widest ${tipoCfg.color}`}>{tipoCfg.label}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none">Stato</span>
                {isEditing ? (
                  <select
                    name="stato"
                    value={formData.stato ?? ''}
                    onChange={handleInputChange}
                    className="bg-background border border-border/60 rounded-xl px-3 py-2.5 text-sm font-black uppercase outline-none focus:ring-2 focus:ring-primary/20 hover:border-primary/40 transition-all cursor-pointer"
                  >
                    <option value="">— Seleziona —</option>
                    <option value="PRONTO">PRONTO</option>
                    <option value="IN RIPARAZIONE">IN RIPARAZIONE</option>
                  </select>
                ) : (
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border w-fit ${statoCfg.bg}`}>
                    <span className={`w-2 h-2 rounded-full ${statoCfg.dot}`} />
                    <span className={`text-sm font-black uppercase tracking-widest ${statoCfg.color}`}>{statoCfg.label}</span>
                  </div>
                )}
              </div>
            </div>
          </Sezione>

          <Sezione titolo="Dati Intervento" icon="ClipboardList">
            <div className="grid grid-cols-2 gap-5">
              <Campo label="N° Cilindro"  name="ncilindro"    value={formData.ncilindro}    isEditing={isEditing} onChange={handleInputChange} placeholder="Es. N°15"              />
              <Campo label="Linea"        name="linea"        value={formData.linea}        isEditing={isEditing} onChange={handleInputChange} placeholder="Es. LINEA 1 ..."       />
              <Campo label="Colata"       name="colata"       value={formData.colata}       isEditing={isEditing} onChange={handleInputChange} placeholder="Es. CCA"               />
              <Campo label="Carro Paniera" name="carropaniera" value={formData.carropaniera} isEditing={isEditing} onChange={handleInputChange} placeholder="Es. CARRO PANIERA 1 ..." />
            </div>
          </Sezione>

          <Sezione titolo="Tempistiche" icon="Calendar">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none">Data Intervento</span>
                {isEditing ? (
                  <input
                    type="datetime-local"
                    name="dataintervento"
                    value={toDatetimeLocal(formData.dataintervento)}
                    onChange={handleInputChange}
                    className="bg-background border border-border/60 rounded-xl px-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-primary/20 hover:border-primary/40 transition-all"
                  />
                ) : (
                  <span className="font-mono text-[13px] font-bold text-foreground/85">{fmtDatetime(formData.dataintervento)}</span>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none">Data Rientro</span>
                {isEditing ? (
                  <input
                    type="date"
                    name="datarientro"
                    value={formData.datarientro ?? ''}
                    onChange={handleInputChange}
                    className="bg-background border border-border/60 rounded-xl px-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-primary/20 hover:border-primary/40 transition-all"
                  />
                ) : (
                  <span className="font-mono text-[13px] font-bold text-foreground/85">{fmtDate(formData.datarientro)}</span>
                )}
              </div>
            </div>
          </Sezione>

          <Sezione titolo="Note" icon="FileText">
            <Campo label="Note intervento" name="note" value={formData.note} isEditing={isEditing} onChange={handleInputChange} type="textarea" placeholder="Osservazioni..." />
          </Sezione>

          <Sezione titolo="Metadati Sistema" icon="Database">
            <div className="grid grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none">ID Record</span>
                <span className="font-mono text-[11px] font-bold text-muted-foreground/40 break-all">{item.id}</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none">Creato il</span>
                <span className="font-mono text-[13px] font-bold text-muted-foreground/50">{fmtDatetime(item.created_at)}</span>
              </div>
            </div>
          </Sezione>
        </div>

        {/* ── FOOTER ── */}
        <div className="shrink-0 px-6 md:px-8 py-5 border-t border-border/40 bg-muted/10 flex items-center justify-between gap-3">
          <div className="hidden sm:flex items-center gap-2 text-muted-foreground/30">
            <Icon name="Fingerprint" size={12} />
            <span className="text-[10px] font-bold uppercase tracking-tighter truncate max-w-[220px]">
              CILINDRIPANIERA | {formData.ncilindro || 'N/D'} — {fmtDate(formData.dataintervento)}
            </span>
          </div>

          <div className="flex gap-2.5 w-full sm:w-auto">
            {isEditing && (
              <>
                {/* Elimina — solo in editing */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                  className="px-5 py-3 bg-red-500/10 text-red-500 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all border border-red-500/20 disabled:opacity-50"
                >
                  Elimina
                </button>

                {/* Annulla editing — torna a vista */}
                <button
                  onClick={handleAnnulla}
                  className="px-5 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-muted/80 transition-all border border-border/40"
                >
                  Annulla
                </button>
              </>
            )}

            {/* Chiudi (sivo) — visibile solo quando NON si sta modificando */}
            {!isEditing && (
              <button
                onClick={onClose}
                className="px-5 py-3 bg-muted text-foreground/70 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-muted/80 transition-all border border-border/40"
              >
                Chiudi
              </button>
            )}

            {/* Modifica / Salva Modifiche */}
            <button
              onClick={isEditing ? handleSave : handleEditClick}
              disabled={loading || isCheckingAuth}
              className="flex-1 sm:flex-none px-7 py-3 bg-primary text-primary-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] hover:opacity-90 transition-all shadow-lg shadow-primary/20 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {(loading || isCheckingAuth) ? (
                <>
                  <Icon name="Loader2" size={14} className="animate-spin" />
                  Elaborazione...
                </>
              ) : isEditing ? 'Salva Modifiche' : 'Modifica'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Detagli_Intervento;